/**
 * Placement colors, taken from the "Coworker Visibility" prototype so the native map matches the
 * design without re-deriving the palette.
 *
 * Purple is the prototype's coworker colour (`team`), shown only while the map is filtered to a coworker
 * group so it cannot be mistaken for the favorites' gold; a desk that is taken but whose holder is not
 * a visible coworker is neutral grey — the prototype's "Unavailable" — so the two states are told
 * apart by colour alone, with nothing drawn on the desk (tech plan §C1). Pending/accepted requests
 * are not modelled here.
 */
export const NATIVE_FLOORPLAN_PLACEMENT_COLORS = {
  available: { fill: '#0ca883', stroke: '#09794e', label: '#ffffff' },
  coworker: { fill: '#991bba', stroke: '#58106b', label: '#ffffff' },
  unavailable: { fill: '#9aa3ab', stroke: '#6f7982', label: '#ffffff' },
  yourBooking: { fill: '#044aef', stroke: '#0332a1', label: '#ffffff' },
} as const;

/** Colour set per placement status (see `placement-status.ts`). */
export const PLACEMENT_STATUS_COLORS = {
  available: NATIVE_FLOORPLAN_PLACEMENT_COLORS.available,
  mine: NATIVE_FLOORPLAN_PLACEMENT_COLORS.yourBooking,
  team: NATIVE_FLOORPLAN_PLACEMENT_COLORS.coworker,
  unavailable: NATIVE_FLOORPLAN_PLACEMENT_COLORS.unavailable,
} as const;

/**
 * Focus mode (an active coworker group, §C3): purple is reserved for the group's members — every
 * other occupied desk is plain `unavailable` grey — and free desks step back to 0.6 so the members
 * stand out without the map losing what is bookable. The members carry the colour alone, no ring.
 */
export const NATIVE_FLOORPLAN_FOCUS_MODE = {
  availableOpacity: 0.6,
} as const;

/**
 * Alpha (hex byte, ~35%) applied to a placement's fill so the drawing shows through the box, as it
 * does in the admin builder. The stroke stays opaque so the box edge remains legible.
 */
export const NATIVE_FLOORPLAN_PLACEMENT_FILL_ALPHA = '59';

/** Neutral ground the plan sits on, from the prototype's map viewport. */
export const NATIVE_FLOORPLAN_MAP_BACKGROUND = '#eaeef2';

/** Selection ring color, also from the prototype (`#044aef` at 3.5px). */
export const NATIVE_FLOORPLAN_SELECTED_COLOR = '#044aef';

/** Query param the selected floor (a floorplan uuid) is kept in. */
export const FLOORPLAN_UUID_QUERY_PARAM = 'floorplanUuid';

export const SECTION_QUERY_PARAM = 'filterBySection';

/** Query param holding the resources-page view; absent means the default list. */
export const RESOURCES_VIEW_QUERY_PARAM = 'view';

export const FLOORPLAN_VIEW = 'floorplan';

/**
 * Query param naming the placement a coworker jump focuses. In the url like the floor and the
 * filters: the jump switches floors, and the focus has to survive that navigation and land with it.
 */
export const FOCUS_RESOURCE_QUERY_PARAM = 'focusResource';

/**
 * Query param naming the coworker group the map is focused on (§C3 focus mode). In the url like the
 * section filter: the sheet writes it, the map reads it, and a reload keeps the focus.
 */
export const COWORKER_GROUP_QUERY_PARAM = 'coworkerGroup';

/** Nested route the find-a-coworker sheet opens on, next to the filters' `filter-by`. */
export const COWORKER_SHEET_PATH = 'find-coworker';

/** Zoom a coworker jump lands at, from the prototype's `jumpToCoworker` (`zoom=2.1`). */
export const FLOORPLAN_FOCUS_SCALE = 2.1;

/**
 * Zoom limits and steps, taken from the prototype's map (`_clampScale`, `zoomBy`, `_panMove`).
 * Panning is only possible past 1x, and the offset is capped so the plan cannot be dragged out of
 * sight — the cap grows with the zoom because there is more of the plan to reach.
 */
export const FLOORPLAN_MIN_SCALE = 1;
export const FLOORPLAN_MAX_SCALE = 3.2;
export const FLOORPLAN_ZOOM_IN_STEP = 1.35;
export const FLOORPLAN_ZOOM_OUT_STEP = 0.74;
export const FLOORPLAN_WHEEL_ZOOM_IN_STEP = 1.12;
export const FLOORPLAN_WHEEL_ZOOM_OUT_STEP = 0.9;
export const FLOORPLAN_PAN_LIMIT_PER_SCALE = 200;
export const FLOORPLAN_PAN_LIMIT_BASE = 40;

export const NATIVE_FLOORPLAN_DIMMED_OPACITY = 0.22;

/** Ring a favorite wears while favorites are highlighted — the prototype's `--system-gold`. */
export const NATIVE_FLOORPLAN_FAVORITE_RING = '#ff9901';
